import { HttpInterceptorFn, HttpErrorResponse, HttpRequest, HttpHandlerFn } from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';
import { AuthService } from '../services/auth.service';
import { LoggerService } from '../services/logger.service';

/** Endpoints that should NOT trigger a token refresh on 401 */
const AUTH_ENDPOINTS = ['/login', '/register', '/refresh', '/password/request', '/password/reset', '/email/verify', '/email/resend', '/2fa/verify'];

let isRefreshing = false;

/**
 * HTTP Error Interceptor
 *
 * Globally handles HTTP errors across the entire application:
 * - 401 Unauthorized: Attempts token refresh, then retries original request
 * - 403 Forbidden: Shows appropriate error message
 * - 423 Locked: Account lockout (handled in login component)
 * - 429 Too Many Requests: Rate limiting (handled in login component)
 * - 500+ Server Errors: Shows user-friendly error message
 * - Network Errors: Handles offline/connection issues
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const injector = inject(Injector);
  const logger = inject(LoggerService);
  const transloco = inject(TranslocoService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Handle different types of errors
      if (error.error instanceof ErrorEvent) {
        // Client-side or network error
        logger.error('Network error:', error.error.message);

        return throwError(() => ({
          status: 0,
          error: {
            message: transloco.translate('errors.network'),
            error: 'network_error'
          }
        }));
      } else {
        // Server-side error
        logger.error(`HTTP Error ${error.status}:`, error.error);

        switch (error.status) {
          case 401:
            return handle401(req, next, router, injector, error);

          case 403:
            logger.warn('Access forbidden:', error.error?.message || 'You do not have permission to access this resource');
            break;

          case 423:
            // Account locked - handled in login component
            break;

          case 429:
            // Rate limit exceeded - handled in login component
            break;

          case 500:
          case 502:
          case 503:
          case 504:
            logger.error('Server error:', error.error?.message);
            break;

          default:
            logger.error('Unexpected error:', error);
        }

        return throwError(() => error);
      }
    })
  );
};

/**
 * Handle 401 Unauthorized errors.
 * Attempts a token refresh for non-auth endpoints, then retries the original request.
 * Falls back to clearing auth and redirecting to login on failure.
 */
function handle401(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  router: Router,
  injector: Injector,
  error: HttpErrorResponse
) {
  // Skip refresh for auth-related endpoints
  const isAuthEndpoint = AUTH_ENDPOINTS.some(ep => req.url.includes(ep));
  if (isAuthEndpoint || isRefreshing) {
    handleUnauthorized(router);
    return throwError(() => error);
  }

  isRefreshing = true;

  // Lazily resolve AuthService to avoid circular dependency during bootstrap
  const authService = injector.get(AuthService);

  return authService.refreshToken().pipe(
    switchMap(() => {
      isRefreshing = false;
      // Retry the original request with the new token
      const token = localStorage.getItem('auth_token');
      const retryReq = req.clone({
        setHeaders: { Authorization: `Bearer ${token}` }
      });
      return next(retryReq);
    }),
    catchError((refreshError) => {
      isRefreshing = false;
      handleUnauthorized(router);
      return throwError(() => refreshError);
    })
  );
}

/**
 * Handle 401 Unauthorized errors
 * Clears authentication state and redirects to login
 */
function handleUnauthorized(router: Router): void {
  const currentUrl = router.url;

  const authPages = ['/login', '/register', '/password/request', '/password/reset', '/email/verify'];
  const isOnAuthPage = authPages.some(path => currentUrl.startsWith(path));

  // Clear authentication state
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('auth_token');

  if (!isOnAuthPage) {
    const returnUrl = currentUrl || '/dashboard';
    router.navigate(['/login'], {
      queryParams: { returnUrl }
    });
  }
}
